import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTabsStore, setCollectionTabs } from '../store/tabsStore';
import { useNotificationStore } from '../store/notificationStore';
import { isFolderTab } from '../utils/collectionTabState';
import { saveFolderFields } from './useFolderSave';

/**
 * Save handler for a FOLDER page tab — the folder counterpart of
 * useCollections.saveCollectionFromTab, with the same payload
 * (useCollectionPageDraft) and the same rule that a deferred save names its
 * own tab (`payload.tabId`) so it never lands in whichever tab is active by
 * the time it runs.
 */
export function useFolderPageSave() {
  const { t } = useTranslation();
  return useCallback(async (payload) => {
    const { collectionTabs, activeCollectionTabId } = useTabsStore.getState();
    const tabId = payload?.tabId ?? activeCollectionTabId;
    const tab = collectionTabs.find((x) => x.id === tabId);
    if (!isFolderTab(tab)) return false;
    if (payload.collectionId && String(payload.collectionId) !== String(tab.collectionId)) return false;
    const fields = {
      documentation: payload.documentation ?? '',
      auth: payload.auth,
      variables: payload.variables || [],
      preRequestScript: payload.preRequestScript ?? '',
      testScript: payload.testScript ?? '',
    };
    try {
      await saveFolderFields({ collectionId: tab.collectionId, folderId: tab.folderId, fields });
    } catch (err) {
      useNotificationStore.getState().error(t('collectionPage.folderSaveFailed', { message: err?.message || '' }));
      return false;
    }
    setCollectionTabs((prev) =>
      prev.map((x) => (x.id === tabId ? { ...x, ...fields, section: payload.section ?? x.section, hasUnsavedChanges: false } : x))
    );
    if (!payload.silent) useNotificationStore.getState().success(t('collectionPage.folderSaved'));
    return true;
  }, [t]);
}

export default useFolderPageSave;
